import React, { useState, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Menu,
  MenuItem,
  Button,
  Typography,
  LinearProgress,
  Divider,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import ReplayIcon from "@material-ui/icons/Replay";
import UpdateIcon from "@material-ui/icons/Update";
import RefreshIcon from "@material-ui/icons/Refresh";
import CancelIcon from "@material-ui/icons/Cancel";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import { DataTable, Heading, PrimaryButton } from "../../components";
import {
  useBulkRestartAction,
  useBulkRestartLatestAction,
  useBulkResumeAction,
  useBulkTerminateAction,
  useBulkPauseAction,
  useBulkRetryAction,
  useBulkTerminateWithReasonAction,
} from "../../data/bulkactions";

const useStyles = makeStyles((theme) => ({
  actionBar: {
    display: "flex",
    alignItems: "center",
    paddingRight: 0,
    "&>div, &>p": {
      marginRight: 10,
    },
    width: "100%",
    justifyContent: "space-between",
    position: "relative",
    zIndex: 1100,
  },
  actionButton: {
    fontFamily: "'Poppins', sans-serif",
    textTransform: "none",
    zIndex: 1100,
  },
  menuPaper: {
    marginTop: 8,
    minWidth: 250,
    borderRadius: 8,
    boxShadow: "0px 4px 24px 0px rgba(60,60,60,0.08)",
  },
  menuItemIcon: {
    minWidth: 32,
    marginRight: 12,
    color: "#3B82F6",
  },
  dialogRoot: {
    fontFamily: "'Poppins', sans-serif",
  },
  resultHead: {
    fontWeight: 500,
    marginBottom: 8,
  },
  divider: {
    margin: "10px 0 14px 0",
  },
}));

const BulkActionMenu = React.memo(
  ({ anchorEl, classes, onClose, onMenuItemClick }) => {
    return (
      <Menu
        id="bulk-action-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={onClose}
        classes={{ paper: classes.menuPaper }}
      >
        <MenuItem onClick={() => onMenuItemClick("Pause")}>
          <PauseIcon className={classes.menuItemIcon} />
          Pause
        </MenuItem>
        <MenuItem onClick={() => onMenuItemClick("Resume")}>
          <PlayArrowIcon className={classes.menuItemIcon} />
          Resume
        </MenuItem>
        <MenuItem onClick={() => onMenuItemClick("RestartCurrent")}>
          <ReplayIcon className={classes.menuItemIcon} />
          Restart with current definitions
        </MenuItem>
        <MenuItem onClick={() => onMenuItemClick("RestartLatest")}>
          <UpdateIcon className={classes.menuItemIcon} />
          Restart with latest definitions
        </MenuItem>
        <MenuItem onClick={() => onMenuItemClick("Retry")}>
          <RefreshIcon className={classes.menuItemIcon} />
          Retry
        </MenuItem>
        <MenuItem onClick={() => onMenuItemClick("Terminate")}>
          <CancelIcon className={classes.menuItemIcon} />
          Terminate
        </MenuItem>
        <MenuItem onClick={() => onMenuItemClick("TerminateWithReason")}>
          <ReportProblemIcon className={classes.menuItemIcon} />
          Terminate with Reason
        </MenuItem>
      </Menu>
    );
  }
);

const TerminateReasonDialog = React.memo(
  ({ open, onClose, onChange, onConfirm, reason, classes }) => {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="terminate-reason-dialog"
        classes={{ paper: classes.dialogRoot }}
      >
        <DialogTitle id="terminate-reason-dialog">
          Terminate with Reason
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Termination Reason"
            type="text"
            fullWidth
            value={reason}
            onChange={onChange}
            variant="outlined"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={onConfirm}
            color="primary"
            variant="contained"
            disabled={!reason}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

const ResultsDialog = React.memo(
  ({ open, onClose, results, isLoading, classes }) => {
    return (
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClose={onClose}
        aria-labelledby="bulk-result-dialog"
        classes={{ paper: classes.dialogRoot }}
      >
        <DialogTitle id="bulk-result-dialog">
          <Typography variant="h5" style={{ padding: 2, fontWeight: 600 }}>
            Batch Actions
          </Typography>
          {isLoading && <LinearProgress />}
        </DialogTitle>
        <DialogContent>
          {results && (
            <>
              <Typography
                variant="subtitle1"
                className={classes.resultHead}
                color="primary"
              >
                Successful Operations
              </Typography>
              <DataTable
                title=""
                columns={[{ name: "workflowId" }]}
                data={results.bulkSuccessfulResults}
                pagination={false}
                showColumnSelector={false}
              />
              <Divider className={classes.divider} />
              <Typography
                variant="subtitle1"
                className={classes.resultHead}
                color="error"
              >
                Failed Operations
              </Typography>
              <DataTable
                title=""
                columns={[
                  { name: "workflowId" },
                  { name: "message", wrap: true },
                ]}
                data={results.bulkErrorResults}
                pagination={false}
                showColumnSelector={false}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </DialogActions>
      </Dialog>
    );
  }
);

const BulkActionModule = ({ selectedRows }) => {
  const classes = useStyles();
  const selectedIds = useMemo(
    () => selectedRows.map((row) => row.workflowId),
    [selectedRows]
  );
  const [results, setResults] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [terminateReason, setTerminateReason] = useState("");

  const onSuccess = useCallback((data) => {
    const retval = {
      bulkErrorResults: Object.entries(data.bulkErrorResults).map(
        ([key, value]) => ({ workflowId: key, message: value })
      ),
      bulkSuccessfulResults: data.bulkSuccessfulResults.map((value) => ({
        workflowId: value,
      })),
    };
    setResults(retval);
  }, []);

  const { mutate: pauseAction, isLoading: pauseLoading } = useBulkPauseAction({
    onSuccess,
  });
  const { mutate: resumeAction, isLoading: resumeLoading } =
    useBulkResumeAction({ onSuccess });
  const { mutate: restartCurrentAction, isLoading: restartCurrentLoading } =
    useBulkRestartAction({ onSuccess });
  const { mutate: restartLatestAction, isLoading: restartLatestLoading } =
    useBulkRestartLatestAction({ onSuccess });
  const { mutate: retryAction, isLoading: retryLoading } = useBulkRetryAction({
    onSuccess,
  });
  const { mutate: terminateAction, isLoading: terminateLoading } =
    useBulkTerminateAction({ onSuccess });
  const {
    mutate: terminateWithReasonAction,
    isLoading: terminateWithReasonLoading,
  } = useBulkTerminateWithReasonAction({ onSuccess });

  const isLoading =
    pauseLoading ||
    resumeLoading ||
    restartCurrentLoading ||
    restartLatestLoading ||
    retryLoading ||
    terminateLoading ||
    terminateWithReasonLoading;

  const handleMenuItemClick = useCallback(
    (action) => {
      setAnchorEl(null);
      const body = JSON.stringify(selectedIds);
      switch (action) {
        case "Pause":
          pauseAction({ body });
          break;
        case "Resume":
          resumeAction({ body });
          break;
        case "RestartCurrent":
          restartCurrentAction({ body });
          break;
        case "RestartLatest":
          restartLatestAction({ body });
          break;
        case "Retry":
          retryAction({ body });
          break;
        case "Terminate":
          terminateAction({ body });
          break;
        case "TerminateWithReason":
          setTerminateReason("");
          setReasonDialogOpen(true);
          break;
        default:
          break;
      }
    },
    [
      selectedIds,
      pauseAction,
      resumeAction,
      restartCurrentAction,
      restartLatestAction,
      retryAction,
      terminateAction,
    ]
  );

  const handleReasonConfirm = useCallback(() => {
    terminateWithReasonAction({
      body: JSON.stringify(selectedIds),
      reason: terminateReason,
    });
    setReasonDialogOpen(false);
  }, [selectedIds, terminateReason, terminateWithReasonAction]);

  const handleCloseResults = useCallback(() => {
    setResults(null);
  }, []);

  const handleMenuOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleReasonDialogClose = useCallback(() => {
    setReasonDialogOpen(false);
  }, []);

  const handleReasonChange = useCallback((event) => {
    setTerminateReason(event.target.value);
  }, []);

  return (
    <div className={classes.actionBar}>
      <Heading level={0}>{selectedRows.length} Workflows Selected.</Heading>
      <div>
        <Button
          className={classes.actionButton}
          variant="contained"
          color="primary"
          aria-controls="bulk-action-menu"
          aria-haspopup="true"
          endIcon={<MoreVertIcon />}
          onClick={handleMenuOpen}
        >
          Bulk Action
        </Button>
        <BulkActionMenu
          anchorEl={anchorEl}
          classes={classes}
          onClose={handleMenuClose}
          onMenuItemClick={handleMenuItemClick}
        />
      </div>

      <TerminateReasonDialog
        open={reasonDialogOpen}
        onClose={handleReasonDialogClose}
        onChange={handleReasonChange}
        onConfirm={handleReasonConfirm}
        reason={terminateReason}
        classes={classes}
      />

      {(results || isLoading) && (
        <ResultsDialog
          open={true}
          onClose={handleCloseResults}
          results={results}
          isLoading={isLoading}
          classes={classes}
        />
      )}
    </div>
  );
};

export default React.memo(BulkActionModule);
